import { Currency, Interval } from '../Data/PricingData';
import axios from 'axios';
import { Mode } from '../types/mode';
import { UploadDocumentResponse, EditMessageRequest, RegenerateMessageRequest, SendMessageRequest, AnswerQuestionRequest } from '../types/api';
import {
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  GoogleDriveFilesResponse,
  InitialPayloadResponse,
  StreamResponse
} from '../types/api';
import { SubscriptionPlan } from '../types/subscription';
import { ClientMessage, MessageImage } from '@/types/message';
import {
  MemoryImportCommitItem,
  MemoryImportCommitResponse,
  MemoryImportPreview,
} from '../types/memory';

/** Opts into live `block` upserts in place of the legacy tool_action/image events. */
const BLOCKS_STREAM_HEADER = { 'X-StudyBuddy-Stream': 'blocks' };

export const API_URL = 'https://api.studdybuddy.ca';

export const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Token management with memory storage but no global variables
let cachedToken: string | null = null;
let tokenExpiry: number | null = null;
let tokenFetchPromise: Promise<string> | null = null;
export let getAccessTokenSilentlyFunc: (() => Promise<string>) | null = null;

export function setGetAccessTokenSilently(getToken: () => Promise<string>) {
  getAccessTokenSilentlyFunc = getToken;
}

let onAuthFailureFunc: (() => void) | null = null;

export function setOnAuthFailure(handler: () => void) {
  onAuthFailureFunc = handler;
}

// Debounced: concurrent 401s must not trigger multiple Auth0 redirects.
let authFailureFired = false;
function triggerAuthFailure() {
  if (authFailureFired) return;
  authFailureFired = true;
  try {
    onAuthFailureFunc?.();
  } finally {
    setTimeout(() => { authFailureFired = false; }, 5000);
  }
}

// Secure token cache with expiration
export function setCachedToken(token: string, expiresInSeconds: number = 3600) {
  cachedToken = token;
  tokenExpiry = Date.now() + (expiresInSeconds * 1000);
}

export function getCachedToken(): string | null {
  if (!cachedToken || !tokenExpiry || Date.now() >= tokenExpiry) {
    cachedToken = null;
    tokenExpiry = null;
    return null;
  }
  return cachedToken;
}

export function clearCachedToken() {
  cachedToken = null;
  tokenExpiry = null;
  tokenFetchPromise = null;
}

// Smart token retrieval with caching
export async function getTokenSmart(): Promise<string | null> {
  // First try cached token
  const cached = getCachedToken();
  if (cached) {
    return cached;
  }
  
  // Prevent multiple simultaneous token fetches
  if (tokenFetchPromise) {
    try {
      return await tokenFetchPromise;
    } catch (error) {
      tokenFetchPromise = null;
      throw error;
    }
  }
  
  // If no cached token, get fresh one
  if (getAccessTokenSilentlyFunc) {
    tokenFetchPromise = getAccessTokenSilentlyFunc();
    try {
      const token = await tokenFetchPromise;
      if (token) {
        setCachedToken(token, 3600); // Cache for 1 hour
        tokenFetchPromise = null;
        return token;
      }
    } catch (error) {
      tokenFetchPromise = null;
      console.error("Error fetching fresh token:", error);
      throw error; // Re-throw to let caller handle the error
    }
  }
  
  return null;
}

axiosInstance.interceptors.request.use(
  async (config: any) => {
    try {
      const token = await getTokenSmart();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      // If no token available, proceed without auth header (let server handle it)
    } catch (error) {
      console.error("Error fetching access token in interceptor:", error);
      // Continue with request even if token fetch fails
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    // 401 is only session-fatal when we actually sent a token. Pre-auth
    // requests with no Authorization header can 401 during setup races.
    if (error?.response?.status === 401 && error?.config?.headers?.Authorization) {
      clearCachedToken();
      triggerAuthFailure();
    }
    return Promise.reject(error);
  }
);

interface MessageResult {
  stream: Stream | Stream[];
}

interface Stream {
  [Symbol.asyncIterator](): AsyncGenerator<any, void, unknown>;
}

export type ClientPlatform = "desktop-web" | "mobile-ios" | "mobile-ipados" | "desktop-macos";

export interface DowntimeState {
  isDowntime: boolean;
  message: string;
  reason: string;
  resolution: string | null;
}

export interface VersionState {
  latest: string;
  minimum: string;
}

export interface AppStateResponse {
  version: VersionState;
  downtime: DowntimeState;
}

export interface requestModel {
  id: string;
}

export const sendMessage = async (
  req: SendMessageRequest
): Promise<MessageResult> => {
  try {
    const { content, mode, useReasoning, useWeb, webCapable, resourceCapable, textFiles, imageFiles, globalBranchCount, requestedModel, abortSignal, chatId, context} = req;
    const formData = new FormData();
    formData.append('content', content);
    formData.append('mode', JSON.stringify(mode));
    formData.append('useReasoning', useReasoning.toString());
    formData.append('useWeb', (useWeb ?? false).toString());
    formData.append('webCapable', (webCapable ?? true).toString());
    formData.append('resourceCapable', (resourceCapable ?? true).toString());
    // This client renders ask_user question cards and can answer them.
    formData.append('askUserCapable', 'true');
    formData.append('context', JSON.stringify(context));
    formData.append('chatId', chatId);

    formData.append('textFiles', JSON.stringify(textFiles));

    formData.append('imageFiles', JSON.stringify(imageFiles));

    formData.append('globalBranchCount', globalBranchCount.toString());
    formData.append('requestedModel', JSON.stringify(requestedModel));

    // Ensure getAccessTokenSilentlyFunc is available before calling
    const token = await getTokenSmart();

    const headers: { [key: string]: string } = {
      // Only add Authorization header if token exists
      ...(token && { 'Authorization': `Bearer ${token}` }),
      'Cache-Control': 'no-cache',
      'Accept': 'text/event-stream',
      ...BLOCKS_STREAM_HEADER
    };

    // Direct fetch without retry mechanism
    const fetchOptions: RequestInit = {
      method: 'POST',
      headers,
      body: formData,
      signal: abortSignal,
      credentials: 'omit', // Changed from 'include' for potentially better CORS handling unless cookies are strictly needed
      mode: 'cors',
      cache: 'no-cache'
    };

    const response = await fetch(`${API_URL}/responses/create`, fetchOptions);

    if (!response.ok) {
      const responseJson = await response.json();
      throw responseJson;
    }

    const stream = streamResponse(response);

    if (!stream) {
      throw new Error("Failed to get stream from response");
    }

    return {
      stream: stream as Stream | Stream[],
    };

  } catch (error: any) {
    throw error;
  }
};

const makeRequest = async (method: string, url: string, data: any = null, config: any = {}) => {
  return axiosInstance({
    method,
    url,
    data,
    ...config,
    keepalive: true,
    timeout: 60000,
    headers: {
      ...config.headers,
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
};

const getClientPlatform = (): ClientPlatform => {
  return "desktop-web";
};

export const getTokenViaAuth0 = async (auth0Token: string): Promise<string> => {
  try {
    const response = await fetch(`${API_URL}/auth/token`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${auth0Token}`,
      },
      credentials: 'include'
    });

    if (response.status === 401) {
      triggerAuthFailure();
      throw new Error('Auth0 token rejected by backend (401)');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.token) {
      throw new Error('Malformed token response: missing token');
    }
    const ttl = ttlFromJwtExp(data.token) ?? data.expiresIn ?? 3600;
    setCachedToken(data.token, ttl);
    return data.token;
  } catch (error: any) {
    console.error('Error fetching token via Auth0:', error);
    throw error;
  }
};

function ttlFromJwtExp(jwt: string): number | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/').padEnd(parts[1].length + (4 - parts[1].length % 4) % 4, '=');
    const payload = JSON.parse(atob(b64));
    if (typeof payload?.exp !== 'number') return null;
    const remaining = payload.exp - Math.floor(Date.now() / 1000);
    return remaining > 0 ? remaining : null;
  } catch {
    return null;
  }
}


export const deleteChat = async (chatId: string): Promise<void> => {
  try {
    const response = await makeRequest('delete', `/chats/${chatId}`);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const renameChat = async (chatId: string, newTitle: string): Promise<void> => {
  const response = await makeRequest('put', `/chats/${chatId}`, { title: newTitle });
  return response.data;
};

export const previewMemoryImport = async (text: string): Promise<MemoryImportPreview> => {
  const response = await makeRequest('post', '/users/memories/import/preview', { text });
  return response.data;
};

export const commitMemoryImport = async (items: MemoryImportCommitItem[]): Promise<MemoryImportCommitResponse> => {
  const response = await makeRequest('post', '/users/memories/import/commit', { items });
  return response.data;
};

export const createCheckoutSession = async (tier: SubscriptionPlan, interval: Interval, currency: Currency, return_url: string, promo_code?: string): Promise<CreateCheckoutSessionResponse> => {
  try {
    const payload: CreateCheckoutSessionRequest = { tier, interval, currency, return_url };
    
    if (promo_code) {
      payload.promo_code = promo_code;
    }

    const response = await makeRequest('post', '/create-checkout-session', payload);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to create checkout session');
    }
  } catch (error: any) {
    // Surface the backend's `{ error, details, ... }` payload on rejected
    // requests so callers can show a meaningful toast instead of the generic
    // axios message.
    const data = error?.response?.data;
    if (data && (data.error || data.details)) {
      const enriched: Error & { details?: string; code?: string; existingCurrency?: string } = new Error(
        data.details || data.error || 'Failed to create checkout session'
      );
      enriched.details = data.details;
      enriched.code = data.error;
      if (data.existingCurrency) enriched.existingCurrency = data.existingCurrency;
      throw enriched;
    }
    throw error;
  }
};

export const handleStripePortalRedirect = async (returnUrl: string): Promise<void> => {
  try {
    const response = await makeRequest('post', '/create-customer-portal-session', { returnUrl });
    const data = response.data;
    if (response.status === 200) {
      window.location.href = data.url;
    } else {
      throw new Error('Failed to redirect to Stripe portal');
    }
  } catch (error: any) {
    throw error;
  }
};

export const regenerateMessage = async (
  req: RegenerateMessageRequest
): Promise<MessageResult> => {
  try {
    const { chatId, messageId, mode, globalBranchCount, useReasoning, useWeb, webCapable, resourceCapable, requestedModel, context, signal } = req;
    const formData = new FormData();
    formData.append('mode', JSON.stringify(mode));
    formData.append('globalBranchCount', globalBranchCount.toString());
    formData.append('useReasoning', useReasoning.toString());
    formData.append('useWeb', (useWeb ?? false).toString());
    formData.append('webCapable', (webCapable ?? true).toString());
    formData.append('resourceCapable', (resourceCapable ?? true).toString());
    // This client renders ask_user question cards and can answer them.
    formData.append('askUserCapable', 'true');
    formData.append('requestedModel', JSON.stringify(requestedModel));
    formData.append('context', JSON.stringify(context));
    formData.append('chatId', chatId);
    formData.append('messageId', messageId);

    const token = await getTokenSmart() || '';

    const headers: { [key: string]: string } = {
      'Authorization': `Bearer ${token}`,
      'Accept': 'text/event-stream',
      ...BLOCKS_STREAM_HEADER,
      'Cache-Control': 'no-cache'
    };

    const fetchOptions: RequestInit = {
      method: 'POST',
      headers,
      body: formData,
      cache: 'no-store',
      keepalive: true,
      ...(signal ? { signal } : {})
    };

    const response = await fetch(
      `${API_URL}/responses/regenerate`,
      fetchOptions
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to regenerate message: ${errorData.message || response.statusText}`);
    }

    const stream = streamResponse(response);
    return { stream };
  } catch (error: any) {
    throw error;
  }
};

/**
 * Answers an `ask_user` question card. The server saves the answers and
 * resumes the same assistant message, streaming only the new content.
 * History is rebuilt from the saved chat server-side, so no context is sent.
 */
export const answerQuestion = async (
  req: AnswerQuestionRequest
): Promise<MessageResult> => {
  const { chatId, messageId, toolCallId, answers, mode, globalBranchCount, useWeb, webCapable, resourceCapable, requestedModel, signal } = req;
  const formData = new FormData();
  formData.append('chatId', chatId);
  formData.append('messageId', messageId);
  formData.append('toolCallId', toolCallId);
  formData.append('answers', JSON.stringify(answers));
  formData.append('context', '[]');
  formData.append('mode', JSON.stringify(mode));
  formData.append('globalBranchCount', globalBranchCount.toString());
  formData.append('useReasoning', 'false');
  formData.append('useWeb', (useWeb ?? false).toString());
  formData.append('webCapable', (webCapable ?? true).toString());
  formData.append('resourceCapable', (resourceCapable ?? true).toString());
  formData.append('askUserCapable', 'true');
  formData.append('requestedModel', JSON.stringify(requestedModel));

  const token = await getTokenSmart() || '';
  const response = await fetch(`${API_URL}/responses/answer`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'text/event-stream',
      ...BLOCKS_STREAM_HEADER,
      'Cache-Control': 'no-cache'
    },
    body: formData,
    cache: 'no-store',
    ...(signal ? { signal } : {})
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw Object.assign(new Error(errorData.message || 'Could not send your answer.'), { status: response.status });
  }

  return { stream: streamResponse(response) };
};

export const sendIncognitoMessage = async (
  req: Omit<SendMessageRequest, 'chatId' | 'globalBranchCount'>
): Promise<MessageResult> => {
  try {
    const { content, mode, useReasoning, useWeb, webCapable, resourceCapable, textFiles, imageFiles, requestedModel, abortSignal, context } = req;
    const formData = new FormData();
    formData.append('content', content);
    formData.append('mode', JSON.stringify(mode));
    formData.append('useReasoning', useReasoning.toString());
    formData.append('useWeb', (useWeb ?? false).toString());
    formData.append('webCapable', (webCapable ?? true).toString());
    formData.append('resourceCapable', (resourceCapable ?? true).toString());
    formData.append('context', JSON.stringify(context));
    formData.append('textFiles', JSON.stringify(textFiles));
    formData.append('imageFiles', JSON.stringify(imageFiles));
    formData.append('requestedModel', JSON.stringify(requestedModel));

    const token = await getTokenSmart();

    const headers: { [key: string]: string } = {
      ...(token && { 'Authorization': `Bearer ${token}` }),
      'Cache-Control': 'no-cache',
      'Accept': 'text/event-stream',
      ...BLOCKS_STREAM_HEADER
    };

    const fetchOptions: RequestInit = {
      method: 'POST',
      headers,
      body: formData,
      signal: abortSignal,
      credentials: 'omit',
      mode: 'cors',
      cache: 'no-cache'
    };

    const response = await fetch(`${API_URL}/responses/incognito`, fetchOptions);

    if (!response.ok) {
      throw await responseError(response);
    }

    const stream = streamResponse(response);

    if (!stream) {
      throw new Error("Failed to get stream from response");
    }

    return {
      stream: stream as Stream | Stream[],
    };
  } catch (error: any) {
    throw error;
  }
};

/**
 * Error for a non-OK response. Rate limits reply with `{ error }` rather than
 * `{ message }`, so normalize both and keep the HTTP status.
 */
const responseError = async (response: Response) => {
  const body = await response.json().catch(() => ({}));
  return { ...body, status: body.status ?? response.status, message: body.message ?? body.error };
};

export const sendGuestMessage = async (context: ClientMessage[], message: string, mode: Mode, signal?: AbortSignal): Promise<StreamResponse> => {
  try {
    const formData = new FormData();
    formData.append('context', JSON.stringify(context));
    formData.append('content', message);
    formData.append('mode', JSON.stringify(mode));
    formData.append('textFiles', "");

    const response = await fetch(`${API_URL}/responses/guest`, {
      method: 'POST',
      headers: {
        'Accept': 'text/event-stream',
        ...BLOCKS_STREAM_HEADER,
        'Cache-Control': 'no-cache',
        'Connection': 'keep-alive'
      },
      body: formData,
      signal,
      credentials: 'include'
    });

    if (!response.ok) {
      throw await responseError(response);
    }

    const stream = streamResponse(response);
    return { stream, response };
  } catch (error: any) {
    console.error('Send guest message error:', error);
    throw error;
  }
};

export const uploadImageAttachment = async (imageFile: File): Promise<MessageImage> => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);

    const token = await getTokenSmart();

    const response = await makeRequest('post', 'attachments/image/upload', formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    const data = await response.data;
    if (!data?.file) {
      throw new Error('No data received from image upload');
    }

    return data.file as MessageImage;
  } catch (error: any) {
    throw error;
  }
};

export const editMessage = async (
  req: EditMessageRequest
): Promise<MessageResult> => {
  try {
    const {
      chatId,
      content,
      mode,
      globalBranchCount,
      useReasoning,
      useWeb,
      webCapable,
      resourceCapable,
      requestedModel,
      context,
      signal,
      imageFiles,
      textFiles,
    } = req;
    const formData = new FormData();
    formData.append('content', content);
    if (imageFiles) formData.append('imageFiles', JSON.stringify(imageFiles));
    if (textFiles) formData.append('textFiles', JSON.stringify(textFiles));
    formData.append('mode', JSON.stringify(mode));
    formData.append('globalBranchCount', globalBranchCount.toString());
    formData.append('useReasoning', useReasoning.toString());
    formData.append('useWeb', (useWeb ?? false).toString());
    formData.append('webCapable', (webCapable ?? true).toString());
    formData.append('resourceCapable', (resourceCapable ?? true).toString());
    // This client renders ask_user question cards and can answer them.
    formData.append('askUserCapable', 'true');
    formData.append('requestedModel', JSON.stringify(requestedModel));
    formData.append('context', JSON.stringify(context))
    formData.append('messageId', req.messageId);
    formData.append('chatId', chatId);
    
    const token = await getTokenSmart() || '';

    const headers: { [key: string]: string } = {
      'Accept': 'text/event-stream',
      ...BLOCKS_STREAM_HEADER,
      'Authorization': `Bearer ${token}`,
      'Cache-Control': 'no-cache',
      'Connection': 'keep-alive'
    };

    const fetchOptions: RequestInit = {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
      ...(signal ? { signal } : {})
    };

    const response = await fetch(
      `${API_URL}/responses/edit`,
      fetchOptions
    );

    const stream = streamResponse(response);
    return { stream };
  } catch (error: any) {
    throw error;
  }
};


// Add this new function with other API functions
export const getInitialPayload = async (): Promise<InitialPayloadResponse> => {
  try {
    const response = await makeRequest('get', '/users/initial-payload');
    return response.data;
  } catch (error: any) {
    throw error;
  }
};


export const uploadDocument = async (file: File): Promise<UploadDocumentResponse> => {
  try {
    const formData = new FormData();
    formData.append('document', file);

    const token = await getTokenSmart();

    const response = await makeRequest('post', '/attachments/document/upload', formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    const data = await response.data;
    if (!data?.file) {
      throw new Error('No data received from document upload');
    }

    return data as UploadDocumentResponse;
  } catch (error: any) {
    console.error('Document upload error:', error);
    throw error;
  }
};

export const generateQuizSuggestions = async (chatContext: any): Promise<string[]> => {
  try {
    const response = await makeRequest('post', '/quiz/suggestions', {
      chatContext
    });

    const suggestions: string[] = response.data;

    // More thorough validation of the response
    if (!suggestions || !Array.isArray(suggestions) || !suggestions.every(item => typeof item === 'string')) {
      console.warn('Invalid quiz suggestions format received:', suggestions);
      return [];
    }

    return suggestions;
  } catch (error: any) {
    console.error('Failed to generate quiz suggestions:', error);
    // Preserve the original error message from the backend
    return [];
  }
};

export const generateQuiz = async (numQuestions: number, questionType: string, difficulty: string, content: string, topic: string, chatId: string): Promise<any> => {
  try {
    const response = await makeRequest('post', '/quiz/generate', {
      numQuestions, questionType, difficulty, content, topic, chatId
    });
    return response.data;
  } catch (error: any) {
    console.error('Error generating quiz:', error);
    throw error;
  }
};

// A helper function used by stream-based endpoints (omitted modifications):
const streamResponse = (response: Response): Stream => {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  
  const processStream = {
    async *[Symbol.asyncIterator]() {
      let isComplete = false;
      try {
        while (!isComplete) {
          let readResult;
          try {
            readResult = await reader.read();
          } catch (readError: unknown) {
            // If the read was aborted, just stop the stream without throwing
            if ((readError as Error).name === 'AbortError') {
              break;
            }
            // For other read errors, rethrow
            throw readError;
          }
          
          const { done, value } = readResult;
          
          if (done) {
            isComplete = true;
            if (buffer.trim()) {
              try {
                const data = JSON.parse(buffer);
                yield data;
              } catch (e) {
                console.warn('Failed to parse final buffer:', e);
              }
            }
            break;
          }
          
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            if (line.trim().startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                yield data;
              } catch (e) {
                console.warn('Failed to parse chunk:', e);
                continue;
              }
            }
          }
        }
      } catch (error: unknown) {
        // Handle other errors but ignore AbortError
        if ((error as Error).name !== 'AbortError') {
          console.error('Stream error:', error);
          throw error;
        } else {
        }
      } finally {
        try {
          if (!reader.closed) {
            await reader.cancel();
          }
        } catch (e) {
          // Only log non-AbortError exceptions
          if (!(e instanceof Error) || e.name !== 'AbortError') {
            console.warn('Error cancelling reader:', e);
          }
        }
      }
    }
  };

  return processStream;
};

export const fixText = async (text: string, improveClarity: boolean): Promise<any> => {
  try {
    const response = await makeRequest('post', '/text/fix', { text, improveClarity });
    const formattedResponse = response.data.correctedText;
    return formattedResponse;
  } catch (error: any) {
    throw error;
  }
};

let appStateRequest: Promise<AppStateResponse | null> | null = null;

export const getAppState = async (): Promise<AppStateResponse | null> => {
  if (appStateRequest) {
    return appStateRequest;
  }

  appStateRequest = fetchAppState();
  return appStateRequest;
};

const fetchAppState = async (): Promise<AppStateResponse | null> => {
  try {
    const response = await makeRequest('post', '/client/version', { platform: getClientPlatform() }, {
      headers: { 'Content-Type': 'application/json' }
    });
    if (
      response.data &&
      response.data.version &&
      typeof response.data.version.latest === 'string' &&
      typeof response.data.version.minimum === 'string' &&
      response.data.downtime &&
      typeof response.data.downtime.isDowntime === 'boolean'
    ) {
      return {
        version: response.data.version,
        downtime: response.data.downtime
      };
    }
    return null;
  } catch (error: any) {
    console.error('Error fetching client version:', error);
    appStateRequest = null;
    return null;
  }
};


// Add this new function to handle the Google Drive OAuth callback
export const exchangeGoogleCode = async (code: string, redirect_uri: string | null): Promise<any> => {
  try {
    // Calling the backend endpoint that exchanges the authorization code for tokens
    const response = await makeRequest('post', '/auth/google/callback', { // Updated endpoint
      code,
      redirect_uri
    });
    return response.data;
  } catch (error: any) {
    console.error('Error exchanging Google code with backend:', error);
    throw error;
  }
};

export const retrieveGoogleDriveFile = async (fileId: string): Promise<Blob> => {
  try {
    const response = await makeRequest('post', 'auth/google/drive/file', { fileId }, { responseType: 'blob' });
    return response.data as Blob;
  } catch (error: any) {
    console.error('Error retrieving Google Drive file blob:', error);
    throw error;
  }
};


export const retrieveGoogleDriveFiles = async (folderId?: string): Promise<GoogleDriveFilesResponse> => {
  try {
    // If a folderId is provided, include it in the request body.
    const data: { folderId?: string } = {};
    if (folderId) data.folderId = folderId;
    const response = await makeRequest('post', 'auth/users/drive/files', data);
    return response.data;
  } catch (error: any) {
    console.error('Error retrieving Google Drive files:', error);
    throw error;
  }
};
